import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { loginRequestSchema, type LoginRequest } from "@/contracts";

import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { FullPageSpinner } from "@/components/ui/spinner";
import { ApiError } from "@/lib/api-error";
import { AuthBrandLayout } from "./auth-brand-layout";

interface LocationState {
  from?: string;
  passwordReset?: boolean;
}

export function LoginPage() {
  const { t } = useTranslation("pages");
  const { status, login } = useAuth();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const state = location.state as LocationState | null;
  const passwordReset = Boolean(state?.passwordReset);

  useEffect(() => {
    document.title = "ZonaClara";
  }, []);

  const redirectTo = state?.from ?? "/dashboard";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: "", password: "" },
  });

  if (status === "loading") {
    return <FullPageSpinner label={t("loginChecking")} />;
  }

  if (status === "authenticated") {
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await login(values);
    } catch (error) {
      setFormError(messageFor(error, t));
    }
  });

  return (
    <AuthBrandLayout title={t("loginHeading")} subtitle={t("loginTagline")}>
      <form
        onSubmit={(event) => void onSubmit(event)}
        noValidate
        className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface p-6 shadow-sm"
      >
        {passwordReset ? (
          <Alert tone="info" title={t("loginPasswordUpdatedTitle")} announce={false}>
            {t("loginPasswordUpdatedBody")}
          </Alert>
        ) : null}

        {formError ? <Alert>{formError}</Alert> : null}

        <TextField
          label={t("loginEmail")}
          type="email"
          autoComplete="username"
          required
          {...(errors.email?.message === undefined ? {} : { error: errors.email.message })}
          {...register("email")}
        />

        <TextField
          label={t("loginPassword")}
          type="password"
          autoComplete="current-password"
          required
          {...(errors.password?.message === undefined ? {} : { error: errors.password.message })}
          {...register("password")}
        />

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm font-medium text-brand-strong hover:underline">
            {t("loginForgotPassword")}
          </Link>
        </div>

        <Button type="submit" loading={isSubmitting}>
          {t("loginSubmit")}
        </Button>

        <p className="text-xs text-ink-muted">{t("loginSessionHint")}</p>
      </form>
    </AuthBrandLayout>
  );
}

function messageFor(
  error: unknown,
  t: (key: "loginFailed" | "loginSuspended" | "loginRateLimited") => string,
): string {
  if (!(error instanceof ApiError)) {
    return t("loginFailed");
  }

  switch (error.code) {
    case "UNAUTHENTICATED":
      return t("loginFailed");
    case "ORGANIZATION_SUSPENDED":
      return t("loginSuspended");
    case "RATE_LIMITED":
      return t("loginRateLimited");
    default:
      return error.message;
  }
}
